/**
 * Records fetch/API call durations for the Super Admin Performance screen.
 * Tracks overall and per-resource (posts, sermons, devotionals, groups, kids church) timings.
 */

const MAX_SAMPLES_PER_RESOURCE = 50;
const MAX_SAMPLES = 100;

const durations: number[] = [];
let lastFetchMs: number | null = null;

/** Display labels for screen data resources we track separately. */
export const FETCH_RESOURCE_LABELS = [
  'posts',
  'sermons',
  'devotionals',
  'groups',
  'kids_church',
] as const;

export type FetchResource = (typeof FETCH_RESOURCE_LABELS)[number];

const resourceBuffers: Record<FetchResource, number[]> = {
  posts: [],
  sermons: [],
  devotionals: [],
  groups: [],
  kids_church: [],
};

function ensureMaxSamples(arr: number[], max: number) {
  while (arr.length > max) arr.shift();
}

export const fetchTimingService = {
  /** Record a completed fetch duration in milliseconds, optionally by resource (table/category). */
  recordFetchMs(ms: number, resource?: FetchResource | null): void {
    lastFetchMs = ms;
    durations.push(ms);
    ensureMaxSamples(durations, MAX_SAMPLES);

    if (resource && FETCH_RESOURCE_LABELS.includes(resource)) {
      resourceBuffers[resource].push(ms);
      ensureMaxSamples(resourceBuffers[resource], MAX_SAMPLES_PER_RESOURCE);
    }
  },

  /** Last single fetch duration in ms, or null if none recorded yet. */
  getLastFetchMs(): number | null {
    return lastFetchMs;
  },

  /** Recent durations (newest last), for sparklines. */
  getRecentDurations(): number[] {
    return [...durations];
  },

  /** Average of recent fetch times in ms; 0 if no samples. */
  getAvgFetchMs(): number {
    if (durations.length === 0) return 0;
    return durations.reduce((a, b) => a + b, 0) / durations.length;
  },

  /** Number of recorded samples (overall). */
  getSampleCount(): number {
    return durations.length;
  },

  /** Per-resource: average ms for a given resource; 0 if no samples. */
  getAvgFetchMsForResource(resource: FetchResource): number {
    const buf = resourceBuffers[resource];
    if (!buf || buf.length === 0) return 0;
    return buf.reduce((a, b) => a + b, 0) / buf.length;
  },

  /** Per-resource: last fetch ms, or null. */
  getLastFetchMsForResource(resource: FetchResource): number | null {
    const buf = resourceBuffers[resource];
    if (!buf || buf.length === 0) return null;
    return buf[buf.length - 1];
  },

  /** Per-resource: recent durations for sparkline. */
  getRecentDurationsForResource(resource: FetchResource): number[] {
    return [...(resourceBuffers[resource] ?? [])];
  },

  /** Per-resource: sample count. */
  getSampleCountForResource(resource: FetchResource): number {
    return (resourceBuffers[resource] ?? []).length;
  },
};
